import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { LuListX, LuLoaderCircle, LuTrash2 } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import { useTranslation } from '../../../i18n/LanguageContext';
import { DetailItem, HelpNote, LoadingRows } from '../../ProfilePage/components/ProfileUi';

/**
 * Seri silme önizlemesi (yalnızca admin): seçilen kayıt ve aynı çalışanın
 * aynı Hasta ID'li (vizit) / aynı başlıklı (iş) sonraki kayıtları. Önizlenen
 * liste ile silinen liste aynıdır.
 */
const CascadeDeleteDialog = ({ cascade, isBuilding, isDeleting, employeeNameOf, projectNameOf, onConfirm, onClose }) => {
  const { t, language, locale } = useTranslation();
  const label = (key, vars) => t(`operations.cascade.${key}`, vars);
  const lastCascade = useRef(cascade);
  if (cascade) lastCascade.current = cascade;
  const shown = cascade || lastCascade.current;
  const isVisit = shown?.type === 'visit';
  const day = (value) => (value ? new Date(value).toLocaleDateString(locale) : '?');
  const count = shown?.items?.length || 0;

  return (
    <Modal
      isOpen={Boolean(cascade)}
      onClose={onClose}
      dismissible={!isDeleting}
      size="lg"
      title={isVisit ? label('titleVisit') : label('titleJob')}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-rose-400 tw-to-rose-600 tw-shadow-rose-500/30" size="xs">
          <LuListX aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={isDeleting}>{label('cancel')}</Button>
          <Button variant="danger" onClick={onConfirm} disabled={isDeleting || isBuilding || count === 0} className="max-sm:tw-flex-1">
            {isDeleting ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {isDeleting ? label('deleting') : label('confirm', { count })}
          </Button>
        </>
      )}
    >
      {shown && (
        <div className="tw-space-y-4">
          <HelpNote tone="danger">{label('warning')}</HelpNote>

          <dl className="tw-grid tw-gap-4 tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-slate-50/70 tw-p-4 sm:tw-grid-cols-3 dark:tw-border-white/10 dark:tw-bg-white/[0.02]">
            <DetailItem label={label('employee')}>{employeeNameOf(shown.anchor.employeeId) || label('unknown')}</DetailItem>
            <DetailItem label={isVisit ? label('patient') : label('jobTitle')}>{shown.seriesKey}</DetailItem>
            <DetailItem label={label('from')}>{label('fromValue', { date: shown.anchorDate.toLocaleDateString(locale) })}</DetailItem>
          </dl>

          {isBuilding ? (
            <LoadingRows count={3} height="tw-h-12" label={label('scanning')} />
          ) : count === 0 ? (
            <p className="tw-rounded-2xl tw-bg-slate-500/[0.06] tw-px-4 tw-py-3 tw-text-sm tw-text-slate-600 dark:tw-text-slate-300">{label('none')}</p>
          ) : (
            <div className="tw-space-y-2">
              <p className="tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{label('count', { count })}</p>
              <ul className="tw-max-h-[45vh] tw-space-y-1.5 tw-overflow-y-auto tw-pr-1 glass-scrollbar">
                {shown.items.map((record) => {
                  const isAnchor = record.id === shown.anchor.id;
                  const recordDate = isVisit ? (record.date || record.firstVisitDate) : record.startDate;
                  return (
                    <li
                      key={record.id}
                      className={`tw-flex tw-items-start tw-gap-3 tw-rounded-xl tw-border tw-px-3 tw-py-2 ${
                        isAnchor ? 'tw-border-rose-500/40 tw-bg-rose-500/[0.06]' : 'tw-border-slate-200/80 tw-bg-white/70 dark:tw-border-white/10 dark:tw-bg-white/[0.03]'
                      }`}
                    >
                      <span className="tw-w-24 tw-shrink-0 tw-pt-0.5 tw-text-xs tw-font-semibold tw-tabular-nums tw-text-slate-600 dark:tw-text-slate-300">{day(recordDate)}</span>
                      <div className="tw-min-w-0 tw-flex-1">
                        <p className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">
                          <span className="tw-min-w-0 tw-truncate">{isVisit ? (projectNameOf(record.projectId) || label('noProject')) : record.title}</span>
                          {isAnchor && <Badge tone="danger">{label('selected')}</Badge>}
                        </p>
                        <p className="tw-mt-0.5 tw-flex tw-flex-wrap tw-gap-x-3 tw-gap-y-0.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                          {isVisit ? (
                            <>
                              {record.visitType && <span>{record.visitType}</span>}
                              {record.visitNumber && <span>{label('visitNumber', { n: record.visitNumber })}</span>}
                              <span>{label('days', { count: record.period || 1 })}</span>
                            </>
                          ) : (
                            <span>{day(record.startDate)} – {record.endDate ? day(record.endDate) : '?'}</span>
                          )}
                          <span className={record.status ? 'tw-font-semibold tw-text-emerald-600 dark:tw-text-emerald-300' : 'tw-font-semibold tw-text-amber-600 dark:tw-text-amber-300'}>
                            {record.status ? label('done') : label('ongoing')}
                          </span>
                        </p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

CascadeDeleteDialog.propTypes = {
  cascade: PropTypes.object,
  isBuilding: PropTypes.bool,
  isDeleting: PropTypes.bool,
  employeeNameOf: PropTypes.func.isRequired,
  projectNameOf: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CascadeDeleteDialog;
